import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import {
  oauthAuthorizePath,
  oauthResourcePath,
  oauthTokenPath,
} from "../../../lib/apiPaths";
import { apiIdentityUrl } from "../../../lib/tenantOrigin";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import { CopyableValue } from "../CopyableValue";
import {
  Card,
  FieldLabel,
  ListBox,
  NeutralIconBadge,
  SectionHeading,
  settingsModalStyle,
} from "../shared";
import { DangerSection } from "../DangerSection";
import { ListTable } from "../../ListTable";

function ConnectionRow({ label, value }: { label: string; value: string }) {
  return (
    <FieldLabel>
      <Box horizontal={8} bottom={8}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      <CopyableValue value={value}>
        <Text ellipsis variant="text-small" color="var(--text-secondary)">
          {value}
        </Text>
      </CopyableValue>
    </FieldLabel>
  );
}

export function OAuthAppDetailModal({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const z = useZero();
  const canManage = useHasAccess("tenant:oauth_apps_manage");
  const externalApiUrl = apiIdentityUrl();
  const [app] = useQuery(queries.oauthApp({ id }));

  const [draftName, setDraftName] = useState<string | null>(null);
  const [draftUris, setDraftUris] = useState<string | null>(null);

  if (!app) return null;

  const saveName = () => {
    if (draftName === null) return;
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== app.name) {
      z.mutate(sharedMutators.updateOAuthApp({ id: app.id, name: trimmed }));
    }
    setDraftName(null);
  };

  const savedUris = (app.redirectUris ?? []).join(" ");
  const saveUris = () => {
    if (draftUris === null) return;
    const parsed = draftUris
      .split(/[\s,]+/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0);
    if (parsed.length > 0 && parsed.join(" ") !== savedUris) {
      z.mutate(
        sharedMutators.updateOAuthApp({ id: app.id, redirectUris: parsed })
      );
    }
    setDraftUris(null);
  };

  const authorizations = app.serviceAccountAuthorizations ?? [];
  const serviceAccountItems = authorizations.map((a) => ({
    key: a.id,
    name: (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <NeutralIconBadge iconName="bot" />
        <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
          {a.serviceAccount?.name ?? a.serviceAccountId}
        </Text>
      </div>
    ),
  }));

  return (
    <Modal open onClose={onClose} style={settingsModalStyle}>
      <SettingsModalHeader>
        <NeutralIconBadge iconName="id-card-lanyard" />
        <Text variant="text-regular-md">{draftName ?? app.name}</Text>
        <HeaderRight>
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </SettingsModalHeader>

      <SettingsModalBody style={{ gap: 24 }}>
        <Card>
          <FieldLabel>
            <Box horizontal={8} bottom={10}>
              <Text variant="text-small-md">Name</Text>
            </Box>
            <Input
              readOnly={!canManage}
              value={draftName ?? app.name}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={saveName}
              style={{ width: "100%" }}
            />
          </FieldLabel>
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Connection</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Configure the app's OAuth client with these values to obtain
              credentials acting as an authorized service account.
            </Text>
          </SectionHeading>
          <Box vertical={4} gap={16}>
            <ConnectionRow label="Client ID" value={app.id} />
            <ConnectionRow
              label="Authorization endpoint"
              value={`${externalApiUrl}${oauthAuthorizePath}`}
            />
            <ConnectionRow
              label="Token endpoint"
              value={`${externalApiUrl}${oauthTokenPath}`}
            />
            <ConnectionRow
              label="Resource"
              value={`${externalApiUrl}${oauthResourcePath}`}
            />
            <FieldLabel>
              <Box horizontal={8} bottom={8}>
                <Text variant="text-small-md">Redirect URIs</Text>
              </Box>
              <Input
                readOnly={!canManage}
                placeholder="https://app.example.com/oauth/callback"
                value={draftUris ?? savedUris}
                onChange={(e) => setDraftUris(e.target.value)}
                onBlur={saveUris}
                style={{ width: "100%" }}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  HTTPS URLs, separated by spaces or commas. The consent flow
                  will only redirect to a registered URI.
                </Text>
              </Box>
            </FieldLabel>
          </Box>
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Authorized service accounts</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Service accounts this app may obtain credentials for. Authorize an
              app from a service account’s OAuth apps section.
            </Text>
          </SectionHeading>
          {serviceAccountItems.length > 0 ? (
            <ListBox data-compact="true" style={{ flex: "none" }}>
              <ListTable
                headers={{ name: "Name" }}
                items={serviceAccountItems}
              />
            </ListBox>
          ) : (
            <Box horizontal={8} vertical={4}>
              <Text variant="text-small" color="var(--text-secondary)">
                No service accounts authorized yet.
              </Text>
            </Box>
          )}
        </Card>

        {canManage && (
          <DangerSection
            title="Delete OAuth app"
            description="Removing this app immediately breaks every service account authorization and credential that uses it. This can’t be undone."
            actionLabel="Delete app"
            confirm={{
              confirmLabel: "Delete",
              description: `Removing “${app.name}” immediately breaks every service account authorization and credential that uses it. This can’t be undone.`,
              onConfirm: () => {
                z.mutate(sharedMutators.deleteOAuthApp({ id: app.id }));
                onClose();
              },
            }}
          />
        )}
      </SettingsModalBody>

      <SettingsModalFooter>
        <Button size="l" onClick={onClose}>
          Done
        </Button>
      </SettingsModalFooter>
    </Modal>
  );
}
